import {
    Analysis,
    Article,
    CodeArticle,
    CollectionName,
    Decision,
    GlossaryTerm,
    Languages,
    LawText,
    PublicationSheet,
    Statistic,
    LinkDocument,
} from '@livv/models';
import {
    getCollectionNameFromModelType,
    getModelTypeFromLawLex,
    getDocumentUrlByCollectionName,
} from '@livv/utils/helpers';
import { SxProps, Theme } from '@mui/material/styles';
import { Translate } from 'next-translate';
import useTranslation from 'next-translate/useTranslation';
import { FC, ReactNode } from 'react';
import CustomLink, { GetTooltipContent } from './CustomLink';
import { getDate } from '@utils/date';
import { stripHtmlTags } from '@utils/parsing';
import { TrackingEventName } from '@utils/types/amplitude/events';

type GetTooltipContentByCollection = (
    collectionName: CollectionName,
    t: Translate,
    lang: Languages,
) => GetTooltipContent<LinkDocument> | undefined;

const getTooltipContentByCollection: GetTooltipContentByCollection = (collectionName, t, lang) => {
    switch (collectionName) {
        case CollectionName.ANALYSES:
            return (data) => {
                const { content } = data as Analysis;

                return {
                    snippet: stripHtmlTags(content),
                    title: '',
                };
            };
        case CollectionName.ARTICLES:
            return (data) => {
                const { content, createdAt, subtitle, title } = data as Article;

                return {
                    snippet: stripHtmlTags(content),
                    subtitles: [subtitle, getDate(createdAt.toDate(), lang)],
                    title,
                };
            };
        case CollectionName.DECISIONS:
            return (data) => {
                const { title, defendant, plaintiff } = data as Decision;

                return {
                    subtitles: [plaintiff.join(', '), defendant.join(', ')],
                    title,
                };
            };
        case CollectionName.CODE_ARTICLES:
            return (data) => {
                const { title, updatedAt, versions } = data as CodeArticle;

                return {
                    snippet: stripHtmlTags(versions[0].texteHtml),
                    subtitles: [`${t('link.versionOf')} ${getDate(updatedAt.toDate(), lang)}`],
                    title,
                };
            };
        case CollectionName.GLOSSARY_TERMS:
            return (data) => {
                const { title, content } = data as GlossaryTerm;

                return {
                    snippet: stripHtmlTags(content.text),
                    title,
                };
            };
        case CollectionName.LAW_TEXTS:
            return (data) => {
                const { title } = data as LawText;

                return {
                    title,
                };
            };
        case CollectionName.PUBLICATION_SHEETS:
            return (data) => {
                const { title, content } = data as PublicationSheet;

                return {
                    snippet: content.blocks.map(({ text }) => text).join('\n'),
                    title, // TODO - VOG-945: add tree title (must be added in data)
                };
            };
        case CollectionName.STATISTICS:
            return (data) => {
                const { title, description } = data as Statistic;

                return {
                    snippet: description,
                    title,
                };
            };
        default:
            return undefined;
    }
};

interface InternalDocumentLinkProps {
    className?: string;
    children: ReactNode;
    dataTestId?: string;
    docId: string;
    hideTooltip?: boolean;
    openInNewTab?: boolean;
    plainLink?: boolean;
    sx?: SxProps<Theme>;
    trackingEventName?: TrackingEventName;
    underlineLinkOnHover?: boolean;
}

const InternalDocumentLink: FC<InternalDocumentLinkProps> = ({
    className,
    children,
    dataTestId,
    docId,
    hideTooltip,
    openInNewTab,
    plainLink = true,
    sx,
    trackingEventName,
    underlineLinkOnHover = true,
}) => {
    const modelType = getModelTypeFromLawLex(docId);
    const { t, lang } = useTranslation('common');
    if (!modelType) {
        return <span data-test-id={dataTestId}>{children}</span>;
    }
    const collectionName = getCollectionNameFromModelType(modelType, docId);
    const getTooltipContent = getTooltipContentByCollection(collectionName, t, lang as Languages);
    const getUrl = getDocumentUrlByCollectionName(collectionName);

    return getTooltipContent && getUrl ? (
        <CustomLink
            className={className}
            dataTestId={dataTestId}
            docId={docId}
            getTooltipContentProps={getTooltipContent}
            getUrl={getUrl}
            hideTooltip={hideTooltip}
            modelType={modelType}
            openInNewTab={openInNewTab}
            plainLink={plainLink}
            sx={sx}
            trackingEventName={trackingEventName}
            underline={underlineLinkOnHover}
        >
            {children}
        </CustomLink>
    ) : (
        <span data-test-id={dataTestId}>{children}</span>
    );
};

export default InternalDocumentLink;
